import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { UserProfile } from '../../types';

interface BarberRegistrationFormProps {
  user: UserProfile | null;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export const BarberRegistrationForm = ({ user, onSubmit, onCancel, isLoading }: BarberRegistrationFormProps) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    gender: '',
    province: '',
    city: '',
    district: '',
    bio: '',
    experience: '',
    specialties: '',
    toolsCompleteness: '',
  });

  React.useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || '',
      }));
    }
  }, [user]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Nama lengkap wajib diisi';
    if (!formData.email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Nomor WhatsApp wajib diisi';
    } else if (!/^\d{9,15}$/.test(formData.phone.replace(/[\s\-\+]/g, ''))) {
       newErrors.phone = 'Format nomor tidak valid (9-15 digit)';
    }
    if (!formData.gender) newErrors.gender = 'Jenis kelamin wajib dipilih';
    if (!formData.province.trim()) newErrors.province = 'Provinsi wajib diisi';
    if (!formData.city.trim()) newErrors.city = 'Kota/Kabupaten wajib diisi';
    if (!formData.district.trim()) newErrors.district = 'Kecamatan wajib diisi';
    if (!formData.experience || parseInt(formData.experience) < 0) {
      newErrors.experience = 'Pengalaman kerja wajib diisi';
    }
    if (!formData.specialties.trim()) newErrors.specialties = 'Sebutkan setidaknya satu keahlian';
    if (!formData.toolsCompleteness) newErrors.toolsCompleteness = 'Kelengkapan alat cukur wajib dipilih';
    if (formData.bio.trim().length < 20) {
      newErrors.bio = 'Bio minimal 20 karakter';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean),
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-20">
      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
        <div className="p-8 md:p-12">
          <div className="mb-10 text-center">
            <div className="w-16 h-16 bg-emerald-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Daftar Jadi Kapster</h2>
            <p className="text-slate-500">Bergabunglah dengan komunitas kapster profesional CukurJe.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Nama Lengkap</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={e => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.name ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="Nama Lengkap"
                />
                {errors.name && <p className="text-xs text-red-500 font-bold ml-1">{errors.name}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Email</label>
                <input 
                  type="email" 
                  value={formData.email}
                  onChange={e => {
                    setFormData({ ...formData, email: e.target.value });
                    if (errors.email) setErrors({ ...errors, email: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.email ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="nama@email.com"
                />
                {errors.email && <p className="text-xs text-red-500 font-bold ml-1">{errors.email}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Jenis Kelamin</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, gender: 'pria' });
                    if (errors.gender) setErrors({ ...errors, gender: '' });
                  }}
                  className={cn(
                    "py-4 rounded-2xl border-2 font-bold text-sm transition-all focus:outline-none flex items-center justify-center gap-2 cursor-pointer",
                    formData.gender === 'pria' 
                      ? "border-emerald-500 bg-emerald-50/50 text-emerald-700 font-extrabold shadow-sm" 
                      : "border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  Barber Pria
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, gender: 'wanita' });
                    if (errors.gender) setErrors({ ...errors, gender: '' });
                  }}
                  className={cn(
                    "py-4 rounded-2xl border-2 font-bold text-sm transition-all focus:outline-none flex items-center justify-center gap-2 cursor-pointer",
                    formData.gender === 'wanita' 
                      ? "border-pink-500 bg-pink-50/50 text-pink-700 font-extrabold shadow-sm" 
                      : "border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-pink-500" />
                  Barber Wanita
                </button>
              </div>
              {errors.gender && <p className="text-xs text-red-500 font-bold ml-1">{errors.gender}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">No. WhatsApp</label>
                <input 
                  type="tel" 
                  value={formData.phone}
                  onChange={e => {
                    setFormData({ ...formData, phone: e.target.value });
                    if (errors.phone) setErrors({ ...errors, phone: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.phone ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="0812..."
                />
                {errors.phone && <p className="text-xs text-red-500 font-bold ml-1">{errors.phone}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Pengalaman (Tahun)</label>
                <input 
                  type="number" 
                  value={formData.experience}
                  onChange={e => {
                    setFormData({ ...formData, experience: e.target.value });
                    if (errors.experience) setErrors({ ...errors, experience: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.experience ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="Contoh: 5"
                />
                {errors.experience && <p className="text-xs text-red-500 font-bold ml-1">{errors.experience}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Provinsi</label>
                <input 
                  type="text" 
                  value={formData.province}
                  onChange={e => {
                    setFormData({ ...formData, province: e.target.value });
                    if (errors.province) setErrors({ ...errors, province: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.province ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="Provinsi"
                />
                {errors.province && <p className="text-xs text-red-500 font-bold ml-1">{errors.province}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Kota/Kabupaten</label>
                <input 
                  type="text" 
                  value={formData.city}
                  onChange={e => {
                    setFormData({ ...formData, city: e.target.value });
                    if (errors.city) setErrors({ ...errors, city: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.city ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="Kota/Kabupaten"
                />
                {errors.city && <p className="text-xs text-red-500 font-bold ml-1">{errors.city}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Kecamatan</label>
                <input 
                  type="text" 
                  value={formData.district}
                  onChange={e => {
                    setFormData({ ...formData, district: e.target.value });
                    if (errors.district) setErrors({ ...errors, district: '' });
                  }}
                  className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.district ? "border-red-200 bg-red-50" : "border-slate-100"
                  )}
                  placeholder="Kecamatan"
                />
                {errors.district && <p className="text-xs text-red-500 font-bold ml-1">{errors.district}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Keahlian (Pisahkan dengan koma)</label>
              <input 
                type="text" 
                value={formData.specialties}
                onChange={e => {
                    setFormData({ ...formData, specialties: e.target.value });
                    if (errors.specialties) setErrors({ ...errors, specialties: '' });
                }}
                className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium",
                    errors.specialties ? "border-red-200 bg-red-50" : "border-slate-100"
                )}
                placeholder="Fade, Classic, Hair Coloring, Shaving"
              />
              {errors.specialties && <p className="text-xs text-red-500 font-bold ml-1">{errors.specialties}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Kelengkapan Alat Cukur</label>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {[
                  { value: 'Lengkap', label: 'Lengkap', color: 'bg-emerald-500' },
                  { value: 'Belum lengkap', label: 'Belum lengkap', color: 'bg-amber-500' },
                  { value: 'Tidak ada', label: 'Tidak ada', color: 'bg-red-500' }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, toolsCompleteness: opt.value });
                      if (errors.toolsCompleteness) setErrors({ ...errors, toolsCompleteness: '' });
                    }}
                    className={cn(
                      "py-4 rounded-2xl border-2 font-bold text-xs sm:text-sm transition-all focus:outline-none flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer",
                      formData.toolsCompleteness === opt.value 
                        ? "border-emerald-500 bg-emerald-50/50 text-emerald-700 font-extrabold shadow-sm" 
                        : "border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100"
                    )}
                  >
                    <span className={cn("w-2 h-2 rounded-full", opt.color)} />
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.toolsCompleteness && <p className="text-xs text-red-500 font-bold ml-1">{errors.toolsCompleteness}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Bio Singkat</label>
              <textarea 
                rows={4}
                value={formData.bio}
                onChange={e => {
                    setFormData({ ...formData, bio: e.target.value });
                    if (errors.bio) setErrors({ ...errors, bio: '' });
                }}
                className={cn(
                    "w-full bg-slate-50 border-2 rounded-2xl px-6 py-4 focus:border-emerald-500 transition-all outline-none text-slate-900 font-medium resize-none",
                    errors.bio ? "border-red-200 bg-red-50" : "border-slate-100"
                )}
                placeholder="Ceritakan sedikit tentang keahlian dan passion Anda di dunia barbering..."
              />
              {errors.bio && <p className="text-xs text-red-500 font-bold ml-1">{errors.bio}</p>}
            </div>

            <div className="flex gap-4 pt-4">
              <button 
                type="button"
                onClick={onCancel}
                className="flex-1 px-8 py-5 rounded-3xl bg-slate-100 text-slate-600 font-bold hover:bg-slate-200 transition-all active:scale-95"
              >
                Batal
              </button>
              <button 
                disabled={isLoading}
                type="submit"
                className="flex-2 px-8 py-5 rounded-3xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-all shadow-xl shadow-emerald-500/20 active:scale-95 disabled:opacity-50"
              >
                {isLoading ? 'Mengirim...' : 'Kirim Pendaftaran'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
